import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import {
  logCheckpointChildUnderGroup,
  logCheckpointKind7FromWire,
} from '@/lib/debug/shape-geometry-debug'
import { GROUP_KIND_ID } from '@/lib/groups/group-kind'
import { readFractionalIndexFromCheckpointElement } from '@/lib/checkpoint/element-wrapper-fields'
import type { DocumentProjection } from '@/lib/canvas/types/document-projection'
import { IDENTITY_PROJECTION } from '@/lib/canvas/document-projection'
import { readWireShapePaint } from '@/lib/elements/read-wire-shape-paint'
import { readTextOnPathConfigFrom } from '@/lib/elements/text-on-path-element'
import type { PathCommand } from '@/lib/elements/types/path-command'
import { pathCommandsForSides, pathCommandsToSvgPath } from '@/lib/elements/shape-path-commands'
import {
  SIDES_ELEMENT_KIND_ID,
  defaultSidesForType,
  innerRatioFromInsetPercent,
  insetPercentFromInner,
} from '@/lib/object-effects/sides'
import { isWrapEligibleKindId, wrapStyleFromBackend } from '@/lib/object-effects/text-wrap'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import { readShapeTextParagraphsFromDecoded } from '@/lib/shapes/read-shape-text-paragraphs'
import type { CheckpointShapeDiagnostics } from './types/checkpoint-shape-diagnostics'
import type { CheckpointToShapesResult } from './types/checkpoint-to-shapes-result'
import { type AnyRecord, asRecord, finiteNumber } from './helpers/tolerant-read'
import {
  geometryDecodedRoot,
  getDecodedData,
  getElementKindId,
  getElementObjectId,
  getNsDocument,
  getUserLayerElements,
  layerChildElements,
} from './helpers/checkpoint-navigation'
import { unwrapElementData } from './helpers/unwrap-element-data'
import { getBaseGeometry } from './helpers/read-base-geometry'
import { readShapeFillImage } from './helpers/read-shape-fill-image'
import {
  SHAPE_TYPES,
  getEditorShapeInsetOrNull,
  getEditorShapeTypeOrNull,
  inferIrregularPolygonShapeType,
  inferRegularPolygonShapeType,
} from './helpers/editor-shape-type'
import {
  getPathCommands,
  isPenPathCommandsClosed,
  offsetPathCommands,
} from './helpers/wire-path-commands'

const OVAL_ELEMENT_KIND_ID = 8

/**
 * Maps checkpoint user-layer elements to existing editor `ShapeData` with minimal assumptions.
 *
 * Current strategy:
 * - If an element has `decodedData.base.{x,y,width,height,rotation}`, render it as a rectangle placeholder (`type: 'square'`).
 * - Use stable `objectId` as the shape id.
 * - `kindId === 1` (RectangleElement) and `kindId === 2` (IrregularPolygonElement) use neutral styling.
 *   Other kinds get an "unsupported" accent styling (except kind 7 groups: transparent chrome like the editor).
 * - Kind 7: recurse into nested `elements` on the wrapper and set `parentGroupId` so the scene graph matches POST /group.
 */
export function checkpointToShapes(
  metadata: unknown,
  projection: DocumentProjection = IDENTITY_PROJECTION
): CheckpointToShapesResult {
  const diagnostics: CheckpointShapeDiagnostics = {
    totalElements: 0,
    renderedElements: 0,
    skippedNoGeometry: 0,
    unsupportedKindIds: {},
  }

  const nsDocument = getNsDocument(metadata)

  if (!nsDocument) {
    return { shapes: [], diagnostics }
  }

  const elements = getUserLayerElements(nsDocument)

  diagnostics.totalElements = elements.length

  const shapes: ShapeData[] = []

  const appendOne = (el: unknown, parentGroupId?: string): void => {
    const element = asRecord(el)

    if (!element) return

    const objectId = getElementObjectId(element)
    const kindId = getElementKindId(element)
    const decodedData = getDecodedData(element)

    if (!objectId || kindId == null || !decodedData) {
      diagnostics.skippedNoGeometry += 1
      return
    }

    const contentDecoded = unwrapElementData(decodedData, kindId)

    const geomRoot = geometryDecodedRoot(contentDecoded, kindId)
    const geomFromFile = getBaseGeometry(geomRoot, projection)

    if (!geomFromFile) {
      diagnostics.skippedNoGeometry += 1
      return
    }

    const geom = geomFromFile

    const isSupportedKind =
      kindId === 1 ||
      kindId === 2 ||
      kindId === 9 ||
      kindId === SIDES_ELEMENT_KIND_ID ||
      kindId === GROUP_KIND_ID ||
      kindId === OVAL_ELEMENT_KIND_ID
    const editorType = getEditorShapeTypeOrNull(contentDecoded, element)
    let shapeType: (typeof SHAPE_TYPES)[number] =
      editorType ??
      (kindId === SIDES_ELEMENT_KIND_ID
        ? inferRegularPolygonShapeType(contentDecoded)
        : kindId === OVAL_ELEMENT_KIND_ID
          ? 'circle'
          : 'square')
    let pathCommands: PathCommand[] | null = null
    const editorInsetRaw = getEditorShapeInsetOrNull(contentDecoded, element)
    const innerRaw = finiteNumber(contentDecoded.inner ?? contentDecoded.Inner)
    const insetForShape =
      kindId === SIDES_ELEMENT_KIND_ID
        ? editorInsetRaw != null
          ? Math.max(0, Math.min(100, Math.round(editorInsetRaw)))
          : innerRaw != null
            ? insetPercentFromInner(innerRaw)
            : undefined
        : undefined

    if (kindId === 2) {
      pathCommands = getPathCommands(contentDecoded)

      if (pathCommands) {
        pathCommands = offsetPathCommands(pathCommands, geom.width / 2, geom.height / 2)
      }

      if (!editorType) {
        shapeType = inferIrregularPolygonShapeType(pathCommands)
      }
    } else if (kindId === 9) {
      pathCommands = getPathCommands(contentDecoded)

      if (pathCommands) {
        pathCommands = offsetPathCommands(pathCommands, geom.width / 2, geom.height / 2)
      }

      shapeType = 'path'
    } else if (kindId === SIDES_ELEMENT_KIND_ID) {
      const sidesRawK = finiteNumber(contentDecoded.sides ?? contentDecoded.Sides)
      const sides =
        sidesRawK != null
          ? Math.max(3, Math.min(100, Math.round(sidesRawK)))
          : defaultSidesForType(shapeType)

      pathCommands = pathCommandsForSides(
        shapeType,
        sides,
        geom.width,
        geom.height,
        insetForShape != null ? innerRatioFromInsetPercent(insetForShape) : undefined,
        // This element is a RegularPolygonElement on the wire, so a 4-sided `square` is the
        // diamond — not the axis-aligned rectangle a never-converted square would render as.
        { asRegularPolygon: true }
      )
    }

    const editorShapeData =
      asRecord(contentDecoded.editorShape) ??
      asRecord(contentDecoded.EditorShape) ??
      asRecord((element as AnyRecord).editorShape)
    const isClosedPenPath =
      kindId === 9 &&
      (editorShapeData?.isClosed === true ||
        contentDecoded.isClosed === true ||
        (pathCommands != null && isPenPathCommandsClosed(pathCommands)))

    const svgPath = pathCommands
      ? pathCommandsToSvgPath(pathCommands, { close: kindId !== 9 || isClosedPenPath })
      : undefined
    const rcRaw =
      kindId === 1
        ? finiteNumber(
            (contentDecoded as { roundedCorner?: unknown }).roundedCorner ??
              (contentDecoded as { RoundedCorner?: unknown }).RoundedCorner
          )
        : null
    const roundedCorner = rcRaw != null ? Math.max(0, Math.min(100, rcRaw)) : undefined
    // `textWrap` always lives at the element's own top level (see `buildRectangleElement` etc. in
    // stowed-element-builder.ts) — a kindId-1 rectangle with a photo fill is not promoted/nested
    // under this architecture (see `legacyImageFields`/`isLegacyBareImage` above), so there's no
    // separate nested-image case to special-case here. A group's `textWrap` lives under
    // `groupElement.textWrap` on the wire, but `unwrapElementData` already merges it onto
    // `contentDecoded` above — only the eligibility gate needs the group carve-out.
    const wrapEligible = isWrapEligibleKindId(kindId) || kindId === GROUP_KIND_ID
    const textWrapRaw = wrapEligible
      ? (asRecord(contentDecoded.textWrap) ?? asRecord(contentDecoded.TextWrap))
      : null
    const wrapStyle = wrapEligible
      ? wrapStyleFromBackend(
          textWrapRaw?.wrapStyle ?? textWrapRaw?.WrapStyle ?? textWrapRaw?.wrap_style
        )
      : undefined
    const wrapOffsetTop =
      finiteNumber(
        textWrapRaw?.wrapOffsetTop ?? textWrapRaw?.WrapOffsetTop ?? textWrapRaw?.wrap_offset_top
      ) ?? undefined
    const wrapOffsetRight =
      finiteNumber(
        textWrapRaw?.wrapOffsetRight ??
          textWrapRaw?.WrapOffsetRight ??
          textWrapRaw?.wrap_offset_right
      ) ?? undefined
    const wrapOffsetBottom =
      finiteNumber(
        textWrapRaw?.wrapOffsetBottom ??
          textWrapRaw?.WrapOffsetBottom ??
          textWrapRaw?.wrap_offset_bottom
      ) ?? undefined
    const wrapOffsetLeft =
      finiteNumber(
        textWrapRaw?.wrapOffsetLeft ?? textWrapRaw?.WrapOffsetLeft ?? textWrapRaw?.wrap_offset_left
      ) ?? undefined
    const fractionalIndex = readFractionalIndexFromCheckpointElement(element)
    const sidesRaw = finiteNumber(contentDecoded.sides ?? contentDecoded.Sides)
    const sidesForShape =
      kindId === SIDES_ELEMENT_KIND_ID
        ? sidesRaw != null
          ? Math.max(3, Math.min(100, Math.round(sidesRaw)))
          : defaultSidesForType(shapeType)
        : undefined

    if (!isSupportedKind) {
      diagnostics.unsupportedKindIds[kindId] = (diagnostics.unsupportedKindIds[kindId] ?? 0) + 1
    }

    const isGroupKind = kindId === GROUP_KIND_ID
    const editorShapeRecord =
      asRecord(contentDecoded.editorShape) ??
      asRecord(contentDecoded.EditorShape) ??
      asRecord((element as AnyRecord).editorShape)
    const photoFill: ShapePhotoFill | undefined = readShapeFillImage(contentDecoded)
    const savedFill = typeof editorShapeRecord?.fill === 'string' ? editorShapeRecord.fill : null
    const savedStroke =
      typeof editorShapeRecord?.stroke === 'string' ? editorShapeRecord.stroke : null
    const savedStrokeWidth = finiteNumber(
      editorShapeRecord?.strokeWidth ?? editorShapeRecord?.strokewidth
    )

    const paragraphs = readShapeTextParagraphsFromDecoded(
      {
        ...(contentDecoded as Record<string, unknown>),
        ...(editorShapeRecord ? { editorShape: editorShapeRecord } : {}),
      },
      kindId
    )

    // editorShape (the frontend-only carrier for savedFill/savedStroke/savedStrokeWidth above) is
    // dropped by the backend on a real save, so a text frame — which every text-unification
    // creation path (drag-create, paste, linked frame, caption) stamps with an explicit
    // transparent fill/stroke precisely so it doesn't render as a visible box — reverts to the
    // "unset shape" visible-gray/black default on the very next reload. Once no saved value
    // survived the round-trip, a paragraphs-bearing shape should default to transparent rather
    // than the plain-shape default; a real color, if it did survive, still wins above.
    const isTextFrameShape = isShapeTextFrame({
      kindId,
      type: shapeType,
      path: svgPath,
      paragraphs,
      photoFill,
    } as ShapeData)

    // ...but `isShapeTextFrame` is true for ANY shape carrying `paragraphs`, so on its own it
    // cannot tell a Type-tool frame (which really should be transparent) from a rectangle the user
    // typed into (which should keep its paint) — and since `savedFill` above is always null after a
    // reload, every shape-with-text came back invisible. `fillStyle`/`lineStyle` are the durable
    // NSD paint attributes, so ask the wire before assuming transparent. `=== false` is
    // load-bearing: `readWireShapePaint` returns `null` for kinds that do not record the attribute
    // (8/9/10), and unknown must mean "keep the paint", not "strip it".
    // Keep in sync with `decodedDataToShape` (use-multiplayer-elements-sync.ts), which mirrors this.
    const wirePaint = readWireShapePaint(contentDecoded, kindId)
    // A fill picked as None (any shape, not just a text frame) is saved with no `fillStyle`, the same
    // as legacy, which also renders a missing `<FS>` as no fill.
    const wireSaysNoFill = wirePaint.hasVisibleFill === false
    const wireSaysNoStroke = isTextFrameShape && wirePaint.hasVisibleStroke === false

    const fill = isGroupKind
      ? 'transparent'
      : (savedFill ??
        (wireSaysNoFill
          ? 'transparent'
          : isSupportedKind
            ? 'rgba(229, 231, 235, 1)'
            : 'rgba(249, 115, 22, 0.08)'))
    const stroke = isGroupKind
      ? 'transparent'
      : (savedStroke ??
        (wireSaysNoStroke ? 'transparent' : isSupportedKind ? '#000000' : 'rgba(249, 115, 22, 1)'))
    const strokeWidth = isGroupKind
      ? 0
      : (savedStrokeWidth ??
        (wireSaysNoStroke
          ? 0
          : isTextFrameShape
            ? // A text frame that kept its stroke restores the exact persisted weight.
              (wirePaint.strokeWeight ?? 1)
            : isSupportedKind
              ? 1
              : 2))

    const textOnPath = readTextOnPathConfigFrom(contentDecoded, decodedData, element)

    const pushedShape: ShapeData = {
      id: objectId,
      ...(fractionalIndex ? { fractionalIndex } : {}),
      type: shapeType,
      x: geom.x,
      y: geom.y,
      width: geom.width,
      height: geom.height,
      kindId,
      ...(pathCommands ? { pathCommands } : {}),
      ...(svgPath ? { path: svgPath } : {}),
      rotation: geom.rotation,
      locked: geom.locked,
      frozen: geom.frozen,
      ignoreTextWrap: geom.ignoreTextWrap,
      fill,
      ...(wirePaint.fillColorId != null ? { fillColorId: wirePaint.fillColorId } : {}),
      ...(wirePaint.fillTint != null ? { fillTint: wirePaint.fillTint } : {}),
      stroke,
      strokeWidth,
      opacity: Math.max(0, Math.min(1, 1 - geom.transparency / 100)),
      ...(roundedCorner !== undefined ? { roundedCorner } : {}),
      ...(wrapStyle !== undefined && wrapStyle !== 'none' ? { wrapStyle } : {}),
      ...(wrapOffsetTop !== undefined ? { wrapOffsetTop } : {}),
      ...(wrapOffsetRight !== undefined ? { wrapOffsetRight } : {}),
      ...(wrapOffsetBottom !== undefined ? { wrapOffsetBottom } : {}),
      ...(wrapOffsetLeft !== undefined ? { wrapOffsetLeft } : {}),
      ...(sidesForShape !== undefined ? { sides: sidesForShape } : {}),
      ...(insetForShape !== undefined ? { inset: insetForShape } : {}),
      ...(parentGroupId !== undefined ? { parentGroupId } : {}),
      ...(paragraphs ? { paragraphs } : {}),
      ...(geom.uid !== undefined ? { uid: geom.uid } : {}),
      ...(photoFill ? { photoFill } : {}),
      // Text on a Path (WMPCM-9542) is written ALONGSIDE the host's own element field rather than
      // promoting it to `kindId: 6`, so it is read back from the same two places: the checkpoint
      // row (which keeps the whole submitted element) and the decoded S3/pending payload. See
      // `lib/elements/text-on-path-element.ts`.
      ...(textOnPath ? { textOnPath } : {}),
    }

    shapes.push(pushedShape)

    if (isGroupKind) {
      logCheckpointKind7FromWire({
        objectId,
        wrapperElement: element,
        decodedData,
        childWrappers: layerChildElements(element, decodedData),
        reconstructedGroup: pushedShape,
      })
    }

    if (parentGroupId && kindId !== GROUP_KIND_ID) {
      logCheckpointChildUnderGroup(parentGroupId, pushedShape)
    }

    if (isGroupKind) {
      for (const child of layerChildElements(element, decodedData)) {
        appendOne(child, objectId)
      }
    }
  }

  for (const el of elements) {
    appendOne(el)
  }

  diagnostics.renderedElements = shapes.length
  return { shapes, diagnostics }
}

export function checkpointToShapeData(metadata: unknown): ShapeData[] {
  return checkpointToShapes(metadata).shapes
}

/**
 * Returns the set of uids belonging to currently-loaded shapes that are acting as text frames —
 * `sanitizeChains`'s sole population of chain-eligible uids (WMPCM-9070). Before text-unification
 * Phase 3, the text-frame population came from a separate `checkpointToText` pass that only
 * surfaced bare kindId-1 rectangles with no `editorShape`; a shape-with-text kept its
 * `editorShape`, so no shape uid was ever included, every shape chain member was dropped on load,
 * and the next link/unlink's wholesale-replace rider deleted the chain server-side. Now that every
 * text frame (bare rectangle or not) is a `ShapeData` shape, this one population is complete.
 *
 * Geometry is irrelevant here (only `uid` and `paragraphs` are read), so this deliberately
 * uses the projection-free `checkpointToShapeData` and needs no book settings.
 */
export function readShapeTextFrameUidsFromCheckpointMetadata(metadata: unknown): Set<string> {
  const uids = new Set<string>()

  for (const shape of checkpointToShapeData(metadata)) {
    if (shape.uid != null && isShapeTextFrame(shape)) uids.add(String(shape.uid))
  }

  return uids
}
